/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useState} from 'react';
import {Preference} from '../../types/preferences';
import UserPreferences from '../UserPreferences';

// defines the result type of usePreferencesState hook...
export type UsePreferencesStateResult = [
  state: any,
  setState: Function,
  preference: any,
  setPreference: Function
];

/**
 * Defining the usePreferencesState hook. This hook is responsible for handling the preference
 * state changes and local state changes that happens when the user do some action with the
 * event which is bind with the preference state.
 *
 * ```typescript
 *
 *  import {usePreferencesState} from '@ion/core/src/preferences/hooks/usePreferencesState';
 *
 *  const [state, setState] = usePreferencesState(Preference.collapsedNavigation);
 *
 * ```
 */
export const usePreferencesState = (
  key: Preference,
  initialState?: any,
  usePreferencesInitialState = true
): UsePreferencesStateResult => {
  const preference = UserPreferences.isInitialized() ? UserPreferences.get(key) : undefined;
  const initialVal = usePreferencesInitialState ? (preference ?? initialState) : initialState;
  const [state, _setState] = useState(initialVal);

  const setPreference = useCallback((value?: any, immediate = true) => {
    UserPreferences.set(key, value, immediate);
  }, [key]);

  const setState = useCallback((
    value: any, updatePreference = true, immediate = !UserPreferences.hasChangeDetection()) => {
    _setState(value);
    updatePreference && setPreference(value, immediate);
  }, [key, _setState, setPreference]);

  return ([
    state,
    setState,
    preference,
    setPreference
  ]);
};

export default usePreferencesState;
